import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly _messageService = inject(MessageService);
  // Resolved when a toast is shown, not at construction: the error interceptor creates this service while
  // TranslateService may still be under construction (it loads its translations over HTTP).
  private readonly _injector = inject(Injector);

  /**
   * Header and message are translation keys: the Dutch text, which the backoffice shows as it is and the portal
   * translates. A text that is already translated has no key and is shown unchanged.
   */
  public showToast(severity: 'success' | 'info' | 'warn' | 'error', header: string, message: string): void {
    this._messageService.add({
      severity,
      summary: this.translate(header),
      detail: this.translate(message),
    });
  }

  public showInfo(message?: string, header?: string): void {
    header ??= 'Informatie';
    message ??= 'Neem kennis van de huidige status';

    this.showToast('info', header, message);
  }

  public showSuccess(message?: string, header?: string): void {
    header ??= 'Gelukt';
    message ??= 'De bewerking is succesvol voltooid';

    this.showToast('success', header, message);
  }

  public showWarning(message?: string, header?: string): void {
    header ??= 'Waarschuwing';
    message ??= 'Wees voorzichtig en controleer uw invoer.';

    this.showToast('warn', header, message);
  }

  public showDanger(message?: string, header?: string): void {
    header ??= 'Fout';
    message ??= 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.';

    this.showToast('error', header, message);
  }

  public showError(message?: string, header?: string): void {
    header ??= 'Fout';
    message ??= 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.';

    this.showToast('error', header, message);
  }

  private translate(text: string): string {
    return text ? this._injector.get(TranslateService).instant(text) : text;
  }
}
